import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { Checklist } from '@cognite/apm-client';

import { classifyNetworkSpeed } from '../../../../utils/classifyNetworkSpeed';
import { MutationKeys, QueryKeys } from '../../../../utils/queryKeys';
import { METRICS_NAMESPACES } from '../../../metrics/constants';

import { getChecklistUpsertMutationContext } from './use-checklist-upsert-mutation.context';

export const useChecklistUpsertMutation = () => {
  const {
    useFDMServices,
    useCurrentUserContext,
    useNetworkStatusContext,
    useMetrics,
    useOtelMetrics,
    makeToast,
    captureException,
  } = useContext(getChecklistUpsertMutationContext());

  const { checklistService } = useFDMServices();
  const queryClient = useQueryClient();
  const { isOnline } = useNetworkStatusContext();

  const { t } = useTranslation('activity');
  const { user } = useCurrentUserContext();
  const metrics = useMetrics(METRICS_NAMESPACES.auditChecklist);
  const sliMetrics = useMetrics(METRICS_NAMESPACES.SLI);

  const { appDataLoadTimeHistogram } = useOtelMetrics();

  const { mutateAsync } = useMutation({
    mutationFn: async (checklist: Checklist) => {
      await checklistService.updateChecklists([checklist], user!);
      return checklist;
    },
    onMutate: async (checklist) => {
      const sliTimerStartTime = isOnline ? Date.now() : undefined;
      const queryCache = queryClient.getQueryCache();

      // Optimistically update the single checklist detail view
      const activeChecklistQuery = queryCache.findAll({
        type: 'active',
        queryKey: [QueryKeys.CHECKLIST],
      });

      if (activeChecklistQuery.length > 0) {
        // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
        await queryClient.cancelQueries({ queryKey: [QueryKeys.CHECKLIST] });

        const [{ queryKey: activeQueryKey }] = activeChecklistQuery;

        // Snapshot the previous value
        const previousChecklist = queryClient.getQueryData(
          activeQueryKey
        ) as Checklist;

        const checklistUpdatedOptimistically = {
          ...previousChecklist,
          ...checklist,
        };

        queryClient.setQueryData(
          activeQueryKey,
          checklistUpdatedOptimistically
        );
      }

      // Always return context to ensure query invalidation runs in onSettled
      return { sliTimerStartTime, cachedIsOnline: isOnline };
    },
    onError: (err, _, context) => {
      const sliTimerEndTime = Date.now();
      const sliTimerMilliseconds = context?.sliTimerStartTime
        ? sliTimerEndTime - context.sliTimerStartTime
        : 0;

      sliMetrics.track(MutationKeys.CHECKLIST_UPDATE, {
        sliTimerMilliseconds,
        status: 'error',
        networkSpeedMbps: navigator.connection?.downlink,
      });

      appDataLoadTimeHistogram.record(sliTimerMilliseconds, {
        success: false,
        networkSpeed: classifyNetworkSpeed(navigator.connection?.downlink),
        feature_name: MutationKeys.CHECKLIST_UPDATE,
      });

      makeToast({
        type: 'danger',
        body: t('CHECKLIST_UPSERT_ERROR', `Unable to update checklist`),
      });
      captureException(err, {
        level: 'error',
        tags: {
          mutationKey: MutationKeys.CHECKLIST_UPDATE,
        },
      });
    },
    onSuccess: async (_, checklist, context) => {
      const sliTimerEndTime = Date.now();

      const sliTimerMilliseconds = context?.sliTimerStartTime
        ? sliTimerEndTime - context.sliTimerStartTime
        : 0;

      sliMetrics.track(MutationKeys.CHECKLIST_UPDATE, {
        sliTimerMilliseconds,
        status: 'success',
        networkSpeedMbps: navigator.connection?.downlink,
      });

      appDataLoadTimeHistogram.record(sliTimerMilliseconds, {
        success: true,
        networkSpeed: classifyNetworkSpeed(navigator.connection?.downlink),
        feature_name: MutationKeys.CHECKLIST_UPDATE,
      });

      metrics.track('Update');
    },
    onSettled: async (_data, _err, _var, context) => {
      // cachedIsOnline is used to check if the user was online when the mutation was triggered
      if (context?.cachedIsOnline) {
        queryClient.invalidateQueries({
          queryKey: [QueryKeys.CHECKLIST],
        });
        queryClient.removeQueries({
          queryKey: [QueryKeys.CHECKLISTS_LIST],
        });
        queryClient.invalidateQueries({
          queryKey: [QueryKeys.OPERATION_CHECKLIST_LIST],
        });
        queryClient.invalidateQueries({
          queryKey: [QueryKeys.SEARCH_CHECKLISTS],
        });
        queryClient.invalidateQueries({
          queryKey: [QueryKeys.SEARCH_ACTIVITIES_WITH_CHECKLIST],
        });
        queryClient.invalidateQueries({
          queryKey: [QueryKeys.ACTIVITY_INFINITE_WITH_CHECKLIST],
        });
        queryClient.invalidateQueries({
          queryKey: [QueryKeys.CHECKLIST_ITEM_STATUS],
        });
      }
    },
    mutationKey: [MutationKeys.UPSERT_CHECKLIST],
    retry: 10,
    retryDelay: 2000,
  });

  return {
    mutateAsync,
  };
};
